import React, { useEffect, useState } from "react";
import TopNavbar from "../../components/AdminPortal/TopNavbar";
import { useSidebar } from "../../context/SidebarContext";
import Details from "../../components/AdminPortal/CRM-approval/Details";
import EvaluationScoreBoard from "../../components/AdminPortal/CRM-approval/EvaluationScoreBoard";
import PersonalDetails from "../../components/AdminPortal/CRM-approval/PersonalDetails";
import FinancialDetails from "../../components/AdminPortal/CRM-approval/FinancialDetails";
import SubmittedDocuments from "../../components/AdminPortal/CRM-approval/SubmittedDocuments";
import { useParams } from "react-router-dom";
import AmountApprove from "../../components/BankingPartnerPortal/AmountApproveBP";
import LoanDetails from "../../components/BankingPartnerPortal/LoanDetails";
import { crmAPI } from "../../services/AdminPortal/crmAPI";
import ReviewActionsBP from "../../components/BankingPartnerPortal/ReviewActionsBP";
import { toast } from "react-toastify";
import { bankingPartnerAPI } from "../../services/BankingPartnerPortal/api";
import { useNavigate } from "react-router-dom";
import ApprovedDetails from "../../components/AdminPortal/CRM-approval/ApprovedDetails";
import { notificationAPI } from "../../services/notificationAPI";
import ConfirmationDialog from "../../components/StaffPortal/ConfirmationDialog";

const LoanApplicationView = () => {
  const { sidebarExpanded, isMobile } = useSidebar();
  const { id: distributionIdFromParams } = useParams(); 
  const navigate = useNavigate();
  const [approveAmount, setApproveAmount] = useState(null);


  const [loading, setLoading] = useState(false);
  const [loanDistribution, setLoanDistribution] = useState(null); // Stores the full LoanDistribution object
  const [documents, setDocuments] = useState([]);
  const [error, setError] = useState(null); // Added error state


  // Derived state for convenience: loanData is the actual loan object
  const loanData = loanDistribution?.loan;
  const [note, setNote] = useState(""); // State for note input

  useEffect(() => {
    const fetchLoanDetails = async () => {
      if (!distributionIdFromParams) return;
      setLoading(true);
      try {
        // Fetch loan details using the distributionId
        const response = await bankingPartnerAPI.getLoanDetailsByDistributionId(
          // API uses distributionId
          distributionIdFromParams
        );
        if (response.success && response.data) {
          // response.data is now the full LoanDistribution object from the backend
          setLoanDistribution(response.data);
        } else {
          throw new Error(response.message || "Loan details not found");
        }
      } catch (error) {
        console.error("Error fetching loan details:", error);
        setError({
          message: error.message || "Failed to load loan details",
          details: error.response?.data || null,
        });
        toast.error(error.message || "Failed to load loan details");
      } finally {
        setLoading(false);
      }
    };
    fetchLoanDetails();
  }, [distributionIdFromParams]);

  useEffect(() => {
    const fetchDocuments = async () => {
      if (!loanData || !loanData._id) return; // Ensure loanData (loan object) and its _id (loanId) are available
      setLoading(true);
      try {
        const documentsData = await crmAPI.getLeadDocuments(loanData._id); // Use loanData._id
        if (!documentsData) {
          throw new Error("Documents not found");
        }
        setDocuments(documentsData);
      } catch (error) {
        console.error("Error fetching documents:", error);
        setError({
          message: error.message || "Failed to load documents",
          details: error.response?.data || null,
        });
        toast.error(error.message || "Failed to load documents");
      } finally {
        setLoading(false);
      }
    };

    if (loanData) {
      // Fetch documents if loanData is available
      fetchDocuments();
    }
  }, [loanData]); // Depends on loanData state

  const handleApplicationStatus = async (status, rejectionReason = null) => {
    // We use distributionIdFromParams directly, as this view is for a specific distribution
    if (!distributionIdFromParams) {
      toast.error("Distribution ID not available to update status.");
      return;
    }

    // Prepare payload
    let payload = {
      status,
      responseNote: note || "", // Use the note state, matching backend expectation
    };

    if (status === "rejected" && rejectionReason) {
      payload.rejectionReason = rejectionReason;
    }

    if (status === "processing") {
      payload.rejectionReason = ""; // Clear rejection reason when reprocessing
    }

    if (status === "approved") {
      const finalApproveAmount = parseFloat(approveAmount);
      if (isNaN(finalApproveAmount) || finalApproveAmount <= 0) {
        toast.error("A valid positive amount is required for approval if 'Custom Amount' is selected, or ensure 'Requested Amount' is correctly set.");
        return; // Stop if amount is invalid for approval
      }
      payload.approveAmount = finalApproveAmount;
    }

    try {
      // Use distributionIdFromParams for this API call
      const response = await bankingPartnerAPI.respondToLoanDistribution(
        distributionIdFromParams, 
        payload
      );
      console.log("Application status updated", response);
      
      toast.success("Application status updated successfully!");
      // Update local state to reflect the change in distribution status
      if (response.success && response.data) {
        console.log("Updating state with new data:", response.data);
        setLoanDistribution((prevDistribution) => {
          const updatedDistribution = {
            ...prevDistribution,
            ...response.data,
            status: response.data.status || status, // Ensure status is updated
          };
          console.log("Updated distribution state:", updatedDistribution);
          return updatedDistribution;
        });
        
        // Refetch data to ensure UI is updated
        try {
          const refreshResponse = await bankingPartnerAPI.getLoanDetailsByDistributionId(distributionIdFromParams);
          if (refreshResponse.success && refreshResponse.data) {
            setLoanDistribution(refreshResponse.data);
          }
        } catch (refreshError) {
          console.error("Error refreshing data:", refreshError);
        }
        
        // Send notification for rejection with reason after successful update
        if (status === "rejected" && rejectionReason) {
          try {
            await notificationAPI.sendNotification(
              loanData?.user?._id,
              `Your loan application has been rejected. Reason: ${rejectionReason}`,
              "Banking Partner",
              loanDistribution?.bankingPartner?._id
            );
          } catch (notificationError) {
            console.error("Error sending notification:", notificationError);
          }
        }
      }
    } catch (error) {
      console.error("Error updating application status:", error);
      toast.error(error.message || "Failed to update application status.");
    }
  };


  const layoutStyles = {
    marginLeft: isMobile ? "0" : sidebarExpanded ? "200px" : "60px",
    width: isMobile
      ? "100%"
      : `calc(100% - ${sidebarExpanded ? "200px" : "60px"})`,
  };

  return (
    <div
      className="min-h-screen bg-black text-white   mt-12"
      style={layoutStyles}
    >
      <TopNavbar />
      <div className="p-4">
        <div className="mb-4 flex flex-wrap items-center justify-start gap-4">
          <h1 className="text-[#FFCA20] font-bold text-xl underline">
            <span
              className="text-[#FFCA20] cursor-pointer"
              onClick={() => navigate("/banking-partner/customers")}
            >
              Loan Applications
            </span>
          </h1>
          <h2 className="text-white font-medium text-md underline">
            Application Review
          </h2>
        </div>
        {/* Display rejection reason if status is rejected */}
        {loanDistribution?.status === "rejected" && loanDistribution?.rejectionReason && (
          <div className="w-full mb-4 p-4 bg-red-900 border border-red-600 rounded-lg">
            <h3 className="text-red-300 font-semibold mb-2">Rejection Reason:</h3>
            <p className="text-white">{loanDistribution.rejectionReason}</p>
          </div>
        )}

        {/* Pass loanData (the nested loan object) to components expecting loan details */}
        <div className="w-full mx-auto">
          <Details application={loanData} distributionStatus={loanDistribution?.status} />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-2 mt-2">
            <PersonalDetails application={loanData} />
            <FinancialDetails loanId={loanData?._id} application={loanData}/>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-2 mt-2">
            {loanDistribution?.status === "approved" ? (
              <ApprovedDetails
                distribution={loanDistribution}
                application={loanData}
              />
            ) : (
              <AmountApprove
                approveAmount={approveAmount}
                setApproveAmount={setApproveAmount}
                note={note}
                setNote={setNote}
                requestedAmount={loanData?.principle}
              />
            )}
          </div>
          <EvaluationScoreBoard loanId={loanData?._id} application={loanData} />
          <SubmittedDocuments
            application={loanData} // Pass the full loan application data
            setDocuments={setDocuments}
            leadId={loanData?._id} // Pass loanId from loanData
            documents={documents}
          />
          {/* Conditional rendering based on loanDistribution.status */}
          {console.log("Current loanDistribution status:", loanDistribution?.status)}
          {(loanDistribution?.status === "processing" ||
          loanDistribution?.status === "sent") ? (
            // Show actions if status is 'processing' or 'sent' (pending BP action)
            <ReviewActionsBP
              handleApplicationStatus={handleApplicationStatus}
            />
          ) : loanDistribution?.status === "approved" ? (
            <div className="w-full py-6 text-center bg-green-600 text-white font-bold text-3xl rounded-lg mt-6 shadow-lg">
              APPROVED BY YOU
            </div>
          ) : loanDistribution?.status === "rejected" ? (
            <div className="w-full py-6 text-center bg-red-600 text-white font-bold text-3xl rounded-lg mt-6 shadow-lg">
              REJECTED BY YOU
            </div>
          ) : null}
        </div>
      </div>


    </div>
  );
};

export default LoanApplicationView;
